import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  ChatBubbleLeftIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import AccountMenu from './AccountMenu';
import WorkflowNotificationBell from '../workflows/WorkflowNotificationBell';
import { billing } from '../../services/api';
import { activeTopBarMenu, buildTopBarNav, isNavItemActive } from './platformNav';

/*
 * The app's one bar across the top (Rudy 2026-11-03, design "Option B"): every page is reached
 * from here, the chat opens first under it. Menus with several sections open as a mega menu;
 * below 1100px the whole bar folds into one sheet behind the menu button. The menu model is
 * `buildTopBarNav` (platformNav.js); this file only draws it.
 */

function MenuItem({ item, current, onPick }) {
  return (
    <Link
      to={item.href}
      role="menuitem"
      onClick={onPick}
      aria-current={current ? 'page' : undefined}
      className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-gray-800 ${current ? 'bg-gray-800' : ''}`}
    >
      <span className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-gray-800 bg-gray-950 text-gray-400">
        <item.icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-white">{item.name}</span>
        {item.description ? <span className="block truncate text-xs text-gray-500">{item.description}</span> : null}
      </span>
    </Link>
  );
}

function Dropdown({ menu, pathname, onPick, align = 'left' }) {
  const mega = menu.sections.length > 1;
  const ref = useRef(null);
  const [shift, setShift] = useState(0);
  // A wide menu opened from a button near either edge would run off the window: slide it back in.
  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 12;
    if (rect.left < gap) setShift(gap - rect.left);
    else if (rect.right > window.innerWidth - gap) setShift(window.innerWidth - gap - rect.right);
  }, []);
  return (
    <div
      ref={ref}
      role="menu"
      aria-label={menu.label}
      className={`absolute top-full z-50 mt-1.5 rounded-xl border border-gray-800 bg-gray-900 p-2 shadow-2xl ${align === 'right' ? 'right-0' : 'left-0'} ${mega ? 'grid w-max max-w-[calc(100vw-1.5rem)] gap-x-2' : 'w-72'}`}
      style={{
        ...(mega ? { gridTemplateColumns: `repeat(${menu.sections.length}, 15rem)` } : null),
        ...(shift ? { transform: `translateX(${shift}px)` } : null),
      }}
    >
      {menu.sections.map((section, index) => (
        <div key={section.heading || index} className="flex min-w-0 flex-col">
          {section.heading ? (
            <div className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">{section.heading}</div>
          ) : null}
          {section.items.map((item) => (
            <MenuItem key={item.href} item={item} current={isNavItemActive(pathname, item.href)} onPick={onPick} />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function TopBar({ user, hostname = '', onLogout }) {
  const location = useLocation();
  const pathname = location.pathname;
  const nav = useMemo(() => buildTopBarNav({ user, hostname }), [user, hostname]);
  const current = activeTopBarMenu(nav, pathname);
  const [open, setOpen] = useState(null); // a menu id, or 'sheet'
  const barRef = useRef(null);

  const { data: balanceData } = useQuery(
    ['top-bar-balance', user?.id],
    () => billing.getBalance().then((r) => r.data),
    { enabled: Boolean(user?.id), staleTime: 60_000, retry: false },
  );
  const balance = balanceData?.balance;

  // A page change, Escape, or a click outside the bar closes whatever is open.
  useEffect(() => { setOpen(null); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOpen(null); };
    const onDown = (event) => {
      if (open !== 'sheet' && barRef.current && !barRef.current.contains(event.target)) setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const toggle = (id) => setOpen((value) => (value === id ? null : id));
  const close = () => setOpen(null);

  const navButton = (menu) => {
    const active = current === menu.id;
    const base = 'inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors';
    const tone = menu.admin
      ? `text-primary-400 hover:bg-primary-900/30 ${active || open === menu.id ? 'bg-primary-900/30' : ''}`
      : `${active || open === menu.id ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800/70 hover:text-white'}`;
    if (menu.href) {
      return (
        <Link key={menu.id} to={menu.href} aria-current={active ? 'page' : undefined} className={`${base} ${tone}`}>
          {menu.label}
        </Link>
      );
    }
    return (
      <div key={menu.id} className="relative">
        <button
          type="button"
          onClick={() => toggle(menu.id)}
          aria-haspopup="menu"
          aria-expanded={open === menu.id}
          className={`${base} ${tone}`}
        >
          {menu.admin ? <ShieldCheckIcon className="h-4 w-4" /> : null}
          {menu.label}
          <ChevronDownIcon className="h-3.5 w-3.5" />
        </button>
        {open === menu.id ? <Dropdown menu={menu} pathname={pathname} onPick={close} align={menu.admin ? 'right' : 'left'} /> : null}
      </div>
    );
  };

  const currentLabel = (() => {
    for (const menu of nav.menus) {
      if (menu.href && isNavItemActive(pathname, menu.href)) return menu.label;
      for (const section of menu.sections || []) {
        const hit = section.items.find((i) => isNavItemActive(pathname, i.href));
        if (hit) return hit.name;
      }
    }
    return nav.account.find((i) => isNavItemActive(pathname, i.href))?.name || '';
  })();

  return (
    <>
      <header
        ref={barRef}
        className="sticky top-0 z-40 flex h-14 items-center gap-1 border-b border-gray-800 bg-gray-900/95 px-3 backdrop-blur"
      >
        <button
          type="button"
          onClick={() => toggle('sheet')}
          aria-label="Menu"
          aria-expanded={open === 'sheet'}
          className="grid h-8 w-8 place-items-center rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white min-[1100px]:hidden"
        >
          {open === 'sheet' ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
        </button>

        <Link to="/ai-chat" className="mr-3 flex-none rounded-lg px-1.5 py-1 text-lg font-bold text-gradient hover:bg-gray-800/60" aria-label="Bluebear.ai, back to chat">
          🐻 Bluebear.ai
        </Link>

        <nav aria-label="Main" className="hidden min-w-0 flex-1 items-center gap-0.5 min-[1100px]:flex">
          {nav.menus.map(navButton)}
        </nav>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-400 min-[1100px]:hidden">{currentLabel}</span>

        <div className="ml-auto flex flex-none items-center gap-1">
          <Link
            to="/credits"
            className="mr-1 hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-800 px-3 py-1 text-xs text-gray-400 hover:border-gray-700 sm:inline-flex"
          >
            Credits
            <b className="font-semibold tabular-nums text-white">
              {balance == null ? '…' : Number(balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </b>
          </Link>
          <WorkflowNotificationBell inline />
          <AccountMenu user={user} items={nav.account} onLogout={onLogout} highlighted={current === 'account'} />
        </div>
      </header>

      {open === 'sheet' ? (
        <nav aria-label="Main" className="fixed inset-x-0 bottom-0 top-14 z-40 overflow-y-auto bg-gray-900 px-3 pb-8 pt-2 min-[1100px]:hidden">
          {nav.menus.map((menu) => (menu.href ? (
            <Link
              key={menu.id}
              to={menu.href}
              onClick={close}
              className={`flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium ${isNavItemActive(pathname, menu.href) ? 'bg-gray-800 text-white' : 'text-gray-200 hover:bg-gray-800'}`}
            >
              <ChatBubbleLeftIcon className="h-5 w-5 text-gray-500" />
              {menu.label}
            </Link>
          ) : (
            <section key={menu.id} className="pt-4">
              <h4 className={`mb-1 flex items-center gap-1.5 px-2 text-[11px] font-medium uppercase tracking-wider ${menu.admin ? 'text-primary-400' : 'text-gray-500'}`}>
                {menu.admin ? <ShieldCheckIcon className="h-3.5 w-3.5" /> : null}
                {menu.label}
              </h4>
              {menu.sections.flatMap((s) => s.items).map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={close}
                  className={`flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm ${isNavItemActive(pathname, item.href) ? 'bg-gray-800 font-medium text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                >
                  <item.icon className="h-5 w-5 text-gray-500" />
                  {item.name}
                </Link>
              ))}
            </section>
          )))}
          <section className="pt-4">
            <h4 className="mb-1 px-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">Account</h4>
            {nav.account.map((item) => (
              <Link key={item.href} to={item.href} onClick={close} className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm text-gray-300 hover:bg-gray-800">
                <item.icon className="h-5 w-5 text-gray-500" />
                {item.name}
              </Link>
            ))}
            <button type="button" onClick={() => { close(); onLogout?.(); }} className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-sm text-gray-300 hover:bg-gray-800">
              <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-500" />
              Sign out
            </button>
          </section>
        </nav>
      ) : null}
    </>
  );
}
